import { ChangeDetectorRef, Component, EventEmitter, input, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgbCalendar, NgbDate, NgbDateStruct, NgbDatepickerConfig } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, Observable, Subscription, tap } from 'rxjs';
import { environment as env } from 'src/environments/environment';
import { AttivitaService } from 'src/app/pages/attività/attivita.service';
import { BaseComponent } from '../base-component/base-component.component';

export enum InputType {
  INPUT = 'input',
  TEXTAREA = 'textarea',
  TEXT_AREA = 'textArea',
  SELECT = 'select',
  DATE = 'date',
  CHECKBOX = 'checkbox',
  CHECKLIST = 'checklist',
}

export enum DataType {
  STRING = 'String',
  INT = 'Int',
  DATE = 'Date',
  BOOLEAN = 'boolean',
  INT_ARRAY = 'Int[]',
  OBJECT_ARRAY = 'Object[]',
}

export interface FormInput {
  name?: string;
  labelName: string;
  inputType: InputType;
  dataType: DataType;
  value?: any;
  mandatory: boolean;
  multiRow: boolean;
  multiSelect: boolean;
  quantityField?: boolean;
  quantity?: number;
  group?: number;
  parent?: string;
  url?: string;
  data?: any[];
  bindedLabel?: string;
  readOnly?: boolean;
  multiRowData?: any[];
  col?: string;
  bindValue?: string;
  repeater?: number;
  autoFill?: boolean;
  groupName?: string;
  intervista?: boolean;
  flash?: boolean;
  locked?: boolean;
  errorMessage?: string;
  invalid?: boolean;
}

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrl: './dynamic-form.component.scss',
  providers: [NgbDatepickerConfig],
})
export class DynamicFormComponent extends BaseComponent implements OnChanges {
  @Input() inputs: FormInput[] = [];
  @Input() mode: 'nuovo' | 'modifica' | 'visualizza' = 'visualizza';
  @Input() activityName: string;
  @Input() activityFlowType: 'struttura' | 'medici' | 'attività' = 'attività';
  @Input() activityFlowData: any;
  @Input() userRole: any;
  @Input() user: any;
  @Input() isSaving: boolean;
  @Input() showValidationErrors: boolean = false;
  @Input() showDataFineField: boolean = true;
  @Output() formChange = new EventEmitter<any>();
  @Output() intervalChange = new EventEmitter<any>();
  @Output() changeSave = new EventEmitter<any>();
  @Output() isFormValid = new EventEmitter<boolean>();
  readonly InputType = InputType;
  readonly DataType = DataType;

  private subscriptions: Subscription[] = [];
  private today = new Date();
  private hasProvinceBeenNullOnce = false;
  private validationSubscription: Subscription;
  private savedDoctorId: number | null = null;
  private doctorChangedByUser = false;
  private prodottiConsegnatiFieldConfig: { autofill: boolean; repeater: number; url?: string } | null = null;
  private prodottiConsegnatiFetchId = 0;

  isInvalid: boolean[] = [];
  isInputDisabled: boolean = false;
  formattedDates: any = {};
  isDisabled: any = {};

  groupedInputs: any = {};
  originalInputs: any[] = [];
  listSaggi: any[] = [];
  multiGroupData: any[][] = [];
  multiGroupDataToShow: any[][] = [];

  idDoctor: any = null;
  searchTerm: string = '';
  presentedProductsIds: any[] = [];
  presentedProducts: any[] = [];
  isPresentedProducts: boolean = false;

  @Input() selectedDoctors: number[] = [];
  selectedDoctorsList: any[] = [];
  misProducts: any[] = [];
  misProdottiSelezionati: any[] = [];
  misDoctors: any[] = [];
  @Input() misDoctorsRows: any[] = [];
  filteredDoctors = [...this.misDoctors];
  filteredDoctorsPerRow: { [key: number]: any[] } = {};
  misDoctorsEssays: { [idDoctor: number]: any[] } = {};
  @Input() misDoctorsEssaysSelected: {
    [doctorId: number]: {
      variant?: any;
      saggioId: number | null;
      quantita: number | null;
      locked: boolean;
    }[];
  } = {};
  misDoctorsEssaysFiltered: { [doctorId: number]: any[][] } = {};
  @Input() misDoctorsEssaysUnique: any[] = [];
  misProdottiPresentatiView: any[] = [];
  misSaggiConsegnatiView: any[] = [];
  showMisForm: boolean = false;
  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly http: HttpClient,
    private readonly attivitaService: AttivitaService,
    private readonly calendar: NgbCalendar,
    private readonly config: NgbDatepickerConfig
  ) {
    super();
    const date = new Date();
    config.minDate = { year: date.getFullYear() - 1, month: 1, day: 1 };
    config.maxDate = { year: date.getFullYear() + 1, month: 12, day: 31 };
  }

  override ngOnInit(): void {
    if (this.activityFlowType === 'medici') {
      this.idDoctor = this.activityFlowData.doctorId;
    }
    this.initInputs();
    this.groupInputs();
    this.setupCalendarRestrictions();
    this.setupSubscriptions();
    this.initMisLogic();

    this.validateFormAndEmit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const significantChanges = [
      'inputs',
      'activityName',
      'mode',
      'activityFlowType',
      'activityFlowData',
      'misDoctorsRows',
      'misDoctorsEssaysSelected',
      'misDoctorsEssaysUnique',
      'selectedDoctors',
    ];

    const hasSignificantChange = significantChanges.some((prop) => changes[prop] && !changes[prop].firstChange);

    if (hasSignificantChange) {
      this.resetFormState();

      setTimeout(() => {
        this.initInputs();
        this.groupInputs();
        this.setupCalendarRestrictions();
        this.initMisLogic();
        this.validateFormAndEmit();

        this.cdr.detectChanges();
      }, 0);
    }
  }

  private resetFormState(): void {
    this.groupedInputs = {};
    this.originalInputs = [];
    this.multiGroupData = [];
    this.multiGroupDataToShow = [];

    this.isInvalid = [];
    this.formattedDates = {};
    this.isDisabled = {};

    this.idDoctor = null;
    this.searchTerm = '';
    this.presentedProductsIds = [];
    this.presentedProducts = [];
    this.isPresentedProducts = false;

    this.misProducts = [];
    this.misProdottiSelezionati = [];
    this.misDoctors = [];
    this.misDoctorsRows = [];
    this.misDoctorsEssaysSelected = {};
    this.misProdottiPresentatiView = [];
    this.misSaggiConsegnatiView = [];

    this.hasProvinceBeenNullOnce = false;
    this.savedDoctorId = null;
    this.doctorChangedByUser = false;
    this.prodottiConsegnatiFieldConfig = null;
    this.prodottiConsegnatiFetchId = 0;

    this.attivitaService.$addressSubject.next(null);
    this.attivitaService.$provinceSubject.next(null);

    if (Array.isArray(this.inputs)) {
      this.inputs.forEach((input) => {
        if ('value' in input) {
          input.value = null;
        }
      });
    }
  }

  initInputs() {
    this.initSavedDoctorId();
    this.originalInputs = [...this.inputs];
    this.initProdottiConsegnatiFieldConfig();

    const reorderedInputs: any[] = [];
    const processed = new Set();

    this.inputs.forEach((input) => {
      if (!processed.has(input) && !input.parent) {
        reorderedInputs.push(input);
        processed.add(input);

        const children = this.inputs.filter((child) => child.parent === input.labelName && !processed.has(child));

        children.forEach((child) => {
          reorderedInputs.push(child);
          processed.add(child);
        });
      }
    });
    this.inputs.forEach((input) => {
      if (!processed.has(input)) {
        reorderedInputs.push(input);
      }
    });

    this.inputs = reorderedInputs;
    this.inputs.forEach((input, i) => {
      this.initSingleInput(input, i);
    });

    if (this.activityName === 'Mis' && this.mode !== 'nuovo') {
      this.initMisDataFromInputs();
    } else {
    }

    setTimeout(() => {
      this.emitForm();
    }, 100);
  }

  initSingleInput(input: any, index: number) {
    if (input.labelName === 'Medico Visitato' && this.mode === 'modifica') {
      if (this.userRole?.name == 'ADMIN') {
        input.readOnly = true;
      }
    }
    if (input.labelName === 'Data' && this.mode === 'modifica') {
      const prodottiConsegnatiField = this.inputs.find((f) => f.name === 'idProduct');

      if (prodottiConsegnatiField && prodottiConsegnatiField.value) {
        const hasDeliveredProduct = prodottiConsegnatiField.value.some((p: any) => p.quantity && p.quantity > 0);

        if (hasDeliveredProduct) {
          input.readOnly = true;
        }
      }
    }

    if (input.labelName === 'Provincia' && this.mode === 'modifica') {
      const medicoVisitatoInput = this.inputs.find((i) => i.labelName === 'Medico Visitato');
      if (medicoVisitatoInput && medicoVisitatoInput.value) {
        input.readOnly = false;
      }
    }

    if (input.inputType === InputType.DATE) {
      if (input.value && input.value !== '') {
        let ngbDate: NgbDate;

        if (typeof input.value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input.value)) {
          const [year, month, day] = input.value.split('-').map(Number);
          ngbDate = new NgbDate(year, month, day);
        } else {
          ngbDate = input.value;
        }

        if (this.dateFormatService) {
          this.formattedDates[input.name] = this.dateFormatService.formatDate(ngbDate, true);
        } else {
          this.formattedDates[input.name] = `${ngbDate.day}-${ngbDate.month}-${ngbDate.year}`;
        }
      } else {
        const today = new Date();
        const ngbDate = new NgbDate(today.getFullYear(), today.getMonth() + 1, today.getDate());
        if (this.dateFormatService) {
          this.formattedDates[input.name] = this.dateFormatService.formatDate(ngbDate, true);
        } else {
          this.formattedDates[input.name] = `${ngbDate.day}-${ngbDate.month}-${ngbDate.year}`;
        }
        input.value = this.formattedDates[input.name];
      }
    }

    if (input.inputType === InputType.CHECKBOX) {
      input.value = input.value === true || input.value === 'True';
    }

    if (input.inputType === InputType.SELECT && input.url && input.labelName !== 'Fascia Oraria') {
      const skipProdottiFetchInEdit = input.labelName === 'Prodotti Consegnati' && (this.mode === 'modifica' || this.mode === 'visualizza');
      if (!skipProdottiFetchInEdit) {
        this.fetchInputData(input);
      }
    }

    if (input.inputType === InputType.SELECT && input.multiRow && this.mode === 'nuovo') {
      if (!input.multiRowData) {
        input.multiRowData = [];
      }
      if (input.multiRowData.length === 0) {
        input.multiRowData.push({
          name: input.labelName,
          value: '',
          mandatory: input.mandatory,
          multiSelect: input.multiSelect,
          inputType: input.inputType,
          dataType: input.dataType,
          multiRow: input.multiRow,
          quantityField: input.quantityField,
          quantity: 0,
          intervista: false,
          flash: false,
        });
      }
    } else if (input.inputType === InputType.SELECT && input.multiRow && this.mode !== 'nuovo') {
      if (input.value) {
        let value = input.value;
        if (typeof value === 'string') {
          try {
            value = JSON.parse(value);
          } catch {
            value = [];
          }
        }

        if (!Array.isArray(value)) {
          value = [value];
        }

        const filteredValue = input.labelName === 'Saggi consegnati' ? value.filter((element: any) => element?.quantity > 0) : value;

        input.multiRowData = filteredValue.map((element: any) => ({
          name: input.labelName,
          value: element?.idProduct ?? element,
          mandatory: input.mandatory,
          multiSelect: input.multiSelect,
          inputType: input.inputType,
          dataType: input.dataType,
          quantity: element?.quantity ?? null,
          multiRow: input.multiRow,
          quantityField: input.quantityField,
          intervista: element?.intervista ?? false,
          flash: element?.flash ?? false,
        }));
      }
    }

    if (this.mode !== 'nuovo' && input.parent && input.value) {
      this.handleDependentFieldInEditMode(input, index);
    }
  }

  private handleDependentFieldInEditMode(input: any, index: number) {
    const parentField = this.inputs.find((i) => i.labelName === input.parent);
    if (parentField && parentField.value) {
      setTimeout(() => {
        this.handleFieldDependencies(parentField, this.inputs.indexOf(parentField));
      }, 200);
    }
  }

  initMultiGroupData(groupData: any[]) {
    if (groupData.length === 0) {
      return;
    }

    const exists = this.multiGroupData.some((group) => group === groupData);

    if (!exists) {
      this.multiGroupData = [...this.multiGroupData, groupData];

      groupData.forEach((item: any, i: number) => {
        this.initArrayData(item);
      });
    } else {
    }
  }

  initArrayData(data: any) {
    const inputData = data;

    switch (inputData.inputType) {
      case InputType.DATE:
        if (inputData.value && inputData.value !== '') {
          const date = inputData.value;
          inputData.formattedDate = this.dateFormatService.formatDate(date, true);
        } else {
          const today = new Date();
          inputData.formattedDate = this.dateFormatService.formatDate(new NgbDate(today.getFullYear(), today.getMonth() + 1, today.getDate()), true);
          inputData.value = inputData.formattedDate;
        }
        break;
      case InputType.CHECKBOX:
        inputData.value = inputData.value === true || inputData.value === 'True';
        break;
      case InputType.SELECT:
        if (inputData.url) {
          if (inputData.labelName === 'Prodotti Consegnati' && (this.mode === 'modifica' || this.mode === 'visualizza')) {
            if (inputData.multiRow && Array.isArray(inputData.value) && inputData.value.length > 0) {
              this.initExistingProdottiConsegnati(inputData.value, inputData, inputData.repeater || inputData.value.length);
            }
            this.selectByUrlMultiGroup(inputData, { selectOptionsOnly: true });
          } else {
            this.selectByUrlMultiGroup(inputData);
          }
        }
        if (inputData.quantityField) {
          inputData.quantity = 0;
        }
        break;
    }
  }

  groupInputs() {
    this.groupedInputs = {};

    const normalInputs = this.inputs.filter((input) => !Array.isArray(input) && (isNaN(Number(input.group)) || Number(input.group) === 0));

    const groupedInputs = this.inputs.filter((input) => !Array.isArray(input) && !isNaN(Number(input.group)) && Number(input.group) > 0);
    groupedInputs.sort((a, b) => {
      if (a.inputType === InputType.CHECKBOX && b.inputType !== InputType.CHECKBOX) return 1;
      if (a.inputType !== InputType.CHECKBOX && b.inputType === InputType.CHECKBOX) return -1;
      return 0;
    });

    groupedInputs.forEach((input: any) => {
      const groupKey = Number(input.group);
      if (!this.groupedInputs[groupKey]) {
        this.groupedInputs[groupKey] = [];
      }

      this.groupedInputs[groupKey].push(input);
    });
    this.initMultiGroupData(groupedInputs);

    this.inputs = [...normalInputs];
  }

  setupCalendarRestrictions() {
    if (this.activityName !== 'Attivita Non Lavorativa') {
      if (this.checkIfConsuntivationUnlocked() && this.userRole?.name === 'ISF') {
        this.enableCalendarDates();
      } else {
        this.disableCalendarDates();
      }
    } else this.enableCalendarDates();
  }

  checkIfConsuntivationUnlocked() {
    if (!this.user?.unlockedIsfResponse) return false;
    const startDate = this.user.unlockedIsfResponse.startUnlockDate;
    const endDate = this.user.unlockedIsfResponse.endUnlockDate;
    const isUnlocked = this.user.unlockedIsfResponse.unlocked;
    return isUnlocked && this.today.getTime() >= new Date(startDate).getTime() && this.today.getTime() <= new Date(endDate).getTime();
  }

  disableCalendarDates() {
    this.isDisabled = (date: NgbDateStruct): boolean => {
      const today = this.calendar.getToday();
      const selectedDate = new NgbDate(date.year, date.month, date.day);
      const selectedWeekday = this.calendar.getWeekday(selectedDate);

      const todayJsDate = new Date(today.year, today.month - 1, today.day);
      const mondayOfWeek = new Date(todayJsDate);
      mondayOfWeek.setDate(todayJsDate.getDate() - (todayJsDate.getDay() === 0 ? 6 : todayJsDate.getDay() - 1));

      const selectedJsDate = new Date(selectedDate.year, selectedDate.month - 1, selectedDate.day);
      const mondayOfSelectedWeek = new Date(selectedJsDate);
      mondayOfSelectedWeek.setDate(selectedJsDate.getDate() - (selectedJsDate.getDay() === 0 ? 6 : selectedJsDate.getDay() - 1));

      if (mondayOfSelectedWeek.getTime() !== mondayOfWeek.getTime()) {
        return true;
      }
      const todayWeekday = this.calendar.getWeekday(today);
      const disabledWeekdays = todayWeekday <= 3 && todayWeekday > 0 ? [4, 5, 6, 7] : [1, 2, 3];
      return disabledWeekdays.includes(selectedWeekday);
    };
  }

  enableCalendarDates() {
    this.isDisabled = undefined;
  }

  onDoctorSearch(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    const t = value.toLowerCase();
    this.filteredDoctors = this.misDoctors.filter((d) => d.fullName.toLowerCase().includes(t));
  }

  onDoctorSearchForRow(search: string, rowIndex: number) {
    const lowerSearch = search.toLowerCase();
    this.filteredDoctorsPerRow[rowIndex] = this.misDoctors.filter(
      (doc) => this.selectedDoctors.includes(doc.id) && !doc.isNotWiseEnabled && doc.fullName.toLowerCase().includes(lowerSearch)
    );
  }

  resetFilteredDoctors() {
    this.filteredDoctors = [...this.misDoctors];
  }

  resetFilteredDoctorsForRow(rowIndex: number) {
    this.filteredDoctorsPerRow[rowIndex] = this.misDoctors.filter((doc) => this.selectedDoctors.includes(doc.id) && !doc.isNotWiseEnabled);
  }

  onSearchBackspace(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.value.length > 0) {
      event.stopPropagation();
    }
  }

  onSearchBackspaceForRow(event: Event, rowIndex: number) {
    const input = event.target as HTMLInputElement;
    if (input.value.length > 0) {
      event.stopPropagation();
    }
  }

  setupSubscriptions() {
    this.subscriptions.push(
      this.attivitaService.$addressSubject.subscribe((res) => {
        if (res) {
          this.handleAddressSubjectChange(res);
        }
      })
    );
    this.subscriptions.push(
      this.attivitaService.$provinceSubject.subscribe((res) => {
        this.handleProvinceSubjectChange(res);
      })
    );
    this.subscriptions.push(
      this.attivitaService.$presentedProductSubjects.subscribe((res) => {
        this.presentedProductsIds = res;
      })
    );

    this.subscriptions.push(
      this.attivitaService.$formValidation.subscribe((validation) => {
        if (!validation.isValid && validation.errors?.length === 0) {
          this.validateFormAndEmit();
        }
      })
    );
  }

  handleAddressSubjectChange(res: any) {
    const shouldRefreshProdottiFromWise = this.mode === 'nuovo' || (this.mode === 'modifica' && this.doctorChangedByUser);

    const addressInputs = this.inputs.filter((input) => !Array.isArray(input) && input.parent === 'Medico Visitato');

    addressInputs.forEach((input) => {
      if (this.mode === 'nuovo') {
        const principalAddress = res.addresses.find((addr: any) => addr.principal);
        if (principalAddress) {
          input.value = principalAddress.id;
          this.onAddressChange(res.addresses, input.value);
        } else {
          input.value = null;
        }
      }
      if (this.activityFlowType === 'struttura' || this.activityFlowType === 'medici') {
        if (this.activityFlowData?.addressId) {
          const address = res.addresses.find((addr: any) => addr.id === this.activityFlowData.addressId);
          input.value = address?.id;
        } else {
          input.value = res.addresses[0]?.id;
        }
        this.onAddressChange(res.addresses, input.value);
      }
      if (this.mode === 'modifica' && this.activityFlowType !== 'struttura' && this.activityFlowType !== 'medici') {
        const principalAddress = res.addresses.find((addr: any) => addr.principal) ?? res.addresses[0];
        if (principalAddress) {
          input.value = principalAddress.id;
          this.onAddressChange(res.addresses, input.value);
        }
      }
      input.data = res.addresses;
      this.setBindedLabel(input);
    });

    const productInputs = this.inputs.filter((input) => !Array.isArray(input) && input.labelName === 'Prodotti Consegnati');

    productInputs.forEach((input) => {
      if (res?.id) {
        this.idDoctor = res.id;
        if (this.mode === 'nuovo') {
          input.multiRowData = [];
          this.addRow(this.inputs.indexOf(input));
        }
        if (shouldRefreshProdottiFromWise) {
          this.fetchInputData(input);
        }
      }
    });

    if (this.multiGroupData.length > 0 && shouldRefreshProdottiFromWise) {
      const parentIndex = res.parentIndex;
      if (parentIndex !== undefined) {
        this.multiGroupData[parentIndex][1].data = [];
        this.selectByUrlMultiGroup(this.multiGroupData[parentIndex][1]);
      } else {
        const prodottiConsegnati = this.multiGroupData.flat().find((input: any) => input.labelName === 'Prodotti Consegnati');
        if (prodottiConsegnati) {
          prodottiConsegnati.data = [];
          this.selectByUrlMultiGroup(prodottiConsegnati);
        }
      }
    }

    this.updateDoctorProfileAndSpecialty(res);
    if (this.mode === 'modifica' && res.addresses && res.addresses.length > 0) {
      const addressInput = this.inputs.find((d: any) => d.name === 'indirizzoDiVisita');
      const selectedAddressId =
        this.activityFlowType === 'struttura' || this.activityFlowType === 'medici' ? this.activityFlowData?.addressId : addressInput?.value;
      const selectedAddress = res.addresses.find((addr: any) => addr.id === selectedAddressId) ?? res.addresses[0];
      if (selectedAddress?.province) {
        const provinceInputs = this.inputs.filter((input) => !Array.isArray(input) && input.labelName === 'Provincia');
        provinceInputs.forEach((input) => {
          input.value = selectedAddress.province.id;
          input.readOnly = true;
        });
      }
    }
    this.emitForm();
    this.cdr.detectChanges();
  }

  handleProvinceSubjectChange(res: any) {
    const provinceInputs = this.inputs.filter((input) => !Array.isArray(input) && input.labelName === 'Provincia');

    provinceInputs.forEach((input) => {
      if (res !== null) {
        if (this.mode === 'nuovo') {
          input.value = null;
        }
        input.value = res[0]?.province?.id;
        input.readOnly = true;
      } else {
        if (this.activityFlowType === 'struttura' || this.activityFlowType === 'medici') {
          if (!this.hasProvinceBeenNullOnce) {
            this.hasProvinceBeenNullOnce = true;
          } else if (this.mode === 'nuovo' && this.hasProvinceBeenNullOnce) {
            input.value = null;
          }
        } else {
          if (this.mode === 'nuovo') {
            input.value = null;
          }
        }
        input.readOnly = false;
      }
    });
  }

  updateDoctorProfileAndSpecialty(res: any) {
    if (this.activityName === 'Informazione medica') {
      this.inputs = this.inputs.filter((input) => Array.isArray(input) || (input.labelName !== 'Profilo' && input.labelName !== 'Specialità'));

      const medicoVisitatoIndex = this.inputs.findIndex((input) => !Array.isArray(input) && input.labelName === 'Medico Visitato');
      if (medicoVisitatoIndex !== -1) {
        let insertIndex = medicoVisitatoIndex + 1;

        if (res.profile) {
          this.inputs.splice(insertIndex, 0, {
            dataType: DataType.STRING,
            inputType: InputType.INPUT,
            mandatory: false,
            multiRow: false,
            multiSelect: false,
            labelName: 'Profilo',
            parent: '',
            value: res.profile?.name,
            readOnly: true,
          });
          insertIndex++;
        }

        if (res.speciality && res.speciality.length > 0) {
          const specialities = res.speciality.map((spec: any) => spec?.speciality?.name).join(' - ');
          this.inputs.splice(insertIndex, 0, {
            dataType: DataType.INT,
            inputType: InputType.TEXTAREA,
            mandatory: false,
            multiRow: false,
            multiSelect: true,
            labelName: 'Specialità',
            parent: '',
            data: [],
            value: specialities,
            readOnly: true,
          });
        }
      }
    }
  }

  fetchInputData(input: any) {
    if (
      this.activityName === 'Mis' &&
      (input.labelName === 'Medici' || input.labelName === 'Saggi Consegnati' || input.labelName === 'Prodotti Presentati')
    ) {
      return;
    }

    if (!input.url) return;

    const apiUrl = input.url;
    let url: URL;

    if (input.labelName === 'Medici') {
      url = new URL(env.baseUrl + apiUrl + `&isf=${this.user?.id}`);
    } else if (input.labelName === 'Prodotti Consegnati') {
      if (this.mode === 'nuovo' && (this.idDoctor === null || this.idDoctor === undefined)) {
        return;
      }
      const doctorId = this.getWiseVariantDoctorIdForFetch();
      url = new URL(env.baseUrl + apiUrl + `${doctorId}&idUser=${this.user?.id}`);
    } else if (input.labelName === 'Utente Isf' && this.user?.id) {
      url = new URL(env.baseUrl + apiUrl + `${this.user.id}`);
    } else {
      url = new URL(env.baseUrl + apiUrl);
    }

    if ((this.userRole?.name === 'ISF' || this.userRole?.name === 'SP') && input.labelName === 'Medico Visitato') {
      url.searchParams.set(this.userRole.name.toLowerCase(), this.user.id);
      url.searchParams.set('notWiseEnabled', 'false');
    }

    if (input.labelName === 'Prodotti Presentati' && this.activityName === 'Mis') {
      url.searchParams.set('isf', this.user.id);
    }

    if (this.searchTerm) {
      url.searchParams.set('name', this.searchTerm);
    }
    if (input.labelName === 'Medico Visitato') {
      url.searchParams.set('isf', this.user.id);
      url.searchParams.set('notWiseEnabled', 'false');
    }

    this.applyIncludedId(url, input);

    this.http.get<any>(url.toString()).subscribe({
      next: (res) => {
        if (input.labelName === 'Prodotti Presentati' && this.activityName === 'Mis') {
          input.data = res.data?.misDetail;
        } else {
          input.data = res.data?.list ? res.data.list : res.data;
        }

        if (input.labelName === 'Medico Visitato') {
          this.handleMedicoVisitatoFilters(input);
        }

        this.setBindedLabel(input);

        if (input.labelName === 'Medico Visitato') {
          const inputIndex = this.inputs.findIndex((i) => i.labelName === 'Medico Visitato');
          if (inputIndex !== -1) {
            this.inputs[inputIndex] = { ...this.inputs[inputIndex] };
          }
        }

        this.cdr.detectChanges();
      },
      error: () => {
        input.data = [];
      },
    });
  }

  addSaggiForDoctors(doctorIds: number[]) {
    if (!doctorIds) return;
    doctorIds.forEach((id) => this.addMisSaggioRow(id));
  }

  onDoctorSearchForRowWrapper(event: Event, rowIndex: number) {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.onDoctorSearchForRow(value, rowIndex);
  }

  onSaggioFilter(doctorId: number, index: number, event: Event) {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.filterSaggi(doctorId, index, value);
  }

  filterSaggi(doctorId: number | null, saggioIndex: number, searchTerm: string) {
    if (doctorId == null) return;

    const allSaggi = this.misDoctorsEssays[doctorId] ?? [];
    const filtered = allSaggi.filter((saggio) => saggio.variant?.name.toLowerCase().includes(searchTerm.toLowerCase()));

    if (!this.misDoctorsEssaysFiltered) this.misDoctorsEssaysFiltered = {};
    if (!this.misDoctorsEssaysFiltered[doctorId]) this.misDoctorsEssaysFiltered[doctorId] = [];

    this.misDoctorsEssaysFiltered[doctorId][saggioIndex] = filtered;
  }

  private applyIncludedId(url: URL, input: { url?: string; value?: any }): void {
    if (!input.url || !/includedId=/i.test(input.url)) return;

    const value = input.value;
    if (value === null || value === undefined || value === '') return;

    if (Array.isArray(value)) {
      if (value.length === 0) return;
      url.searchParams.set('IncludedId', value.join(','));
      return;
    }

    url.searchParams.set('IncludedId', String(value));
  }

  private appendMisIncludedIds(urlTemplate: string, url: URL, value: any): void {
    if (!/includedId=/i.test(urlTemplate)) return;
    if (value === null || value === undefined || value === '') return;

    url.searchParams.delete('IncludedId');

    const ids = Array.isArray(value) ? value : [value];
    ids.filter((id) => id !== null && id !== undefined && id !== '').forEach((id) => url.searchParams.append('IncludedId', String(id)));
  }

  private getMisMediciIncludedIds(mediciInput?: { value?: any } | null): any[] | undefined {
    if (this.mode === 'nuovo' || !mediciInput) {
      return undefined;
    }

    const value = mediciInput.value;
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
      return undefined;
    }

    return value;
  }

  private mergeMisDoctors(apiDoctors: any[]): void {
    const doctorsById = new Map<number, any>();

    this.misDoctors.forEach((doctor) => {
      doctorsById.set(Number(doctor.id), doctor);
    });

    apiDoctors.forEach((doctor) => {
      const id = Number(doctor.id);
      const existing = doctorsById.get(id);
      doctorsById.set(id, {
        ...existing,
        ...doctor,
        fullName: doctor.fullName ?? existing?.fullName,
      });
    });

    this.misDoctors = Array.from(doctorsById.values());
  }

  private seedMisDoctorsFromSaggi(saggiValue: any[]): void {
    const doctorsById = new Map<number, any>();

    saggiValue.forEach((saggio) => {
      if (saggio.idDoctor == null || doctorsById.has(saggio.idDoctor)) return;
      doctorsById.set(saggio.idDoctor, {
        id: saggio.idDoctor,
        fullName: saggio.fullName ?? String(saggio.idDoctor),
      });
    });

    if (doctorsById.size > 0) {
      this.misDoctors = Array.from(doctorsById.values());
      this.filteredDoctors = [...this.misDoctors];
    }
  }

  private populateMisSaggiFromInput(saggiValue: any[]): void {
    const groupedByDoctor: Record<string, any[]> = saggiValue.reduce((acc: any, item: any) => {
      if (!acc[item.idDoctor]) acc[item.idDoctor] = [];
      acc[item.idDoctor].push(item);
      return acc;
    }, {});

    this.misDoctorsRows = [{ doctorIds: Object.keys(groupedByDoctor).map((id) => parseInt(id)) }];
    this.filteredDoctorsPerRow = this.misDoctorsRows.map(() => [...this.misDoctors]);

    this.misDoctorsEssaysSelected = {};
    (Object.entries(groupedByDoctor) as [string, any[]][]).forEach(([doctorId, saggi]) => {
      this.misDoctorsEssaysSelected[parseInt(doctorId)] = saggi.map((essay) => ({
        saggioId: essay.idProduct,
        id: essay.idProduct,
        name: essay.nameProduct,
        quantita: essay.quantity,
        locked: this.mode === 'visualizza',
      }));
    });

    this.updateUniqueSaggi();
  }

  private initSavedDoctorId(): void {
    if (this.mode === 'nuovo') {
      this.savedDoctorId = null;
      this.doctorChangedByUser = false;
      return;
    }

    const medicoInput = this.inputs.find((input) => !Array.isArray(input) && input.labelName === 'Medico Visitato');
    this.savedDoctorId = medicoInput?.value ?? null;
    this.doctorChangedByUser = false;
  }

  private initProdottiConsegnatiFieldConfig(): void {
    const field = this.originalInputs.find((input) => !Array.isArray(input) && input.labelName === 'Prodotti Consegnati');

    if (!field) {
      this.prodottiConsegnatiFieldConfig = null;
      return;
    }

    this.prodottiConsegnatiFieldConfig = {
      autofill: this.isAutoFillEnabled(field),
      repeater: field.repeater ?? 1,
      url: field.url,
    };
  }

  private getProdottiConsegnatiFieldConfig(inputData?: any): { autofill: boolean; repeater: number; url?: string } {
    return {
      autofill: this.isAutoFillEnabled(inputData) || !!this.prodottiConsegnatiFieldConfig?.autofill,
      repeater: inputData?.repeater ?? this.prodottiConsegnatiFieldConfig?.repeater ?? 1,
      url: inputData?.url ?? this.prodottiConsegnatiFieldConfig?.url,
    };
  }

  private shouldAutofillProdottiFromWise(): boolean {
    return this.mode === 'nuovo' || (this.mode === 'modifica' && this.doctorChangedByUser);
  }

  private getWiseVariantDoctorIdForFetch(): string {
    if (this.shouldAutofillProdottiFromWise()) {
      return this.idDoctor != null && this.idDoctor !== undefined ? String(this.idDoctor) : '';
    }
    return '';
  }

  private isAutoFillEnabled(inputData: { autoFill?: boolean; autofill?: boolean }): boolean {
    return !!(inputData.autoFill ?? inputData.autofill);
  }

  private mergeProdottiConsegnatiSelectData(apiList: any[], inputData: any): any[] {
    const merged = new Map<number, any>();
    (apiList ?? []).forEach((product) => merged.set(product.id, product));

    if (Array.isArray(inputData.value)) {
      inputData.value.forEach((item: any) => {
        if (item.idProduct != null && !merged.has(item.idProduct)) {
          merged.set(item.idProduct, {
            id: item.idProduct,
            name: item.nameProduct,
          });
        }
      });
    }

    return Array.from(merged.values());
  }

  private applyProdottiConsegnatiSelectData(inputData: any, data: any[]): void {
    inputData.data = data;
    this.setBindedLabel(inputData);

    this.multiGroupData.forEach((row) => {
      row.forEach((field: any) => {
        if (field.labelName === 'Prodotti Consegnati') {
          field.data = data;
          field.bindedLabel = inputData.bindedLabel;
        }
      });
    });
  }

  handleMedicoVisitatoFilters(input: any) {
    if (this.activityFlowType === 'struttura') {
      input.data = input.data.filter(
        (doctor: any) => Array.isArray(doctor.addresses) && doctor.addresses.some((address: any) => address.id === this.activityFlowData?.addressId)
      );
      if (input.data.length == 1) {
        this.onInputChange({ target: { value: input.data[0].id } }, this.inputs.indexOf(input), 'select');
      }
    } else if (this.activityFlowType === 'medici') {
      input.data = input.data.filter((doctor: any) => doctor.id === this.activityFlowData?.doctorId);
      if (input.data[0]) {
        this.onInputChange({ target: { value: input.data[0].id } }, this.inputs.indexOf(input), 'select');
      }
    } else {
      if (input.value !== null && input.value !== undefined) {
        this.onInputChange({ target: { value: input.value } }, this.inputs.indexOf(input), 'select');
      }
    }
  }

  selectByUrlMultiGroup(inputData: any, options?: { selectOptionsOnly?: boolean }) {
    const selectOptionsOnly = options?.selectOptionsOnly ?? false;

    if (!selectOptionsOnly && inputData.data && inputData.data.length > 0) return;

    const apiUrl = inputData.url;
    let url: URL;

    if (inputData.labelName === 'Medici') {
      url = new URL(env.baseUrl + apiUrl + `&isf=${this.user.id}`);
    } else if (inputData.labelName === 'Saggi Consegnati' || inputData.labelName === 'Prodotti Consegnati') {
      const doctorId =
        inputData.labelName === 'Prodotti Consegnati' ? this.getWiseVariantDoctorIdForFetch() : this.idDoctor ? String(this.idDoctor) : '';
      url = new URL(env.baseUrl + apiUrl + `${doctorId}&idUser=${this.user.id}`);
    } else {
      if (apiUrl) {
        url = new URL(env.baseUrl + apiUrl);
      } else return;
    }

    if ((this.userRole?.name === 'ISF' || this.userRole?.name === 'SP') && inputData.labelName === 'Medico Visitato') {
      url.searchParams.set(this.userRole.name.toLowerCase(), this.user.id);
    }

    if (this.searchTerm) {
      url.searchParams.set('name', this.searchTerm);
    }

    this.applyIncludedId(url, inputData);

    const fetchId = ++this.prodottiConsegnatiFetchId;

    this.http.get<any>(url.toString()).subscribe({
      next: (res) => {
        if (fetchId !== this.prodottiConsegnatiFetchId) {
          return;
        }

        const apiList = res.data?.list ? res.data.list : res.data;
        const prodottiConfig = this.getProdottiConsegnatiFieldConfig(inputData);

        if (selectOptionsOnly && inputData.labelName === 'Prodotti Consegnati') {
          const mergedData = this.mergeProdottiConsegnatiSelectData(apiList, inputData);
          this.applyProdottiConsegnatiSelectData(inputData, mergedData);
          this.cdr.detectChanges();
          return;
        }

        const shouldFill =
          inputData.labelName === 'Prodotti Consegnati' &&
          prodottiConfig.autofill &&
          this.shouldAutofillProdottiFromWise() &&
          (this.mode !== 'nuovo' || prodottiConfig.repeater > 0);

        if (shouldFill) {
          this.fillProdottiConsegnati(apiList, inputData, prodottiConfig.repeater);
        }

        inputData.data = apiList;

        if (inputData.labelName === 'Medico Visitato') {
          this.onInputChange(inputData.value, this.inputs.indexOf(inputData), 'select');
        }

        this.setBindedLabel(inputData);

        if (this.multiGroupData.length > 0) {
          this.multiGroupData.forEach((row) => {
            row.forEach((field: any) => {
              if (field.labelName === inputData.labelName) {
                field.data = inputData.data;
                field.bindedLabel = inputData.bindedLabel;
              }
            });
          });
        }
        this.cdr.detectChanges();
      },
      error: () => {
        inputData.data = [];
      },
    });
  }

  fillProdottiConsegnati(data: any, inputData: any, repeater: number) {
    if (!data?.length) return;

    const prodottiConfig = this.getProdottiConsegnatiFieldConfig(inputData);
    const baseRow = JSON.parse(JSON.stringify(this.multiGroupData[0]));
    this.multiGroupData = [];

    data.forEach((item: any, index: number) => {
      if (index >= repeater) {
        return;
      }

      const rowToAdd = JSON.parse(JSON.stringify(baseRow));
      rowToAdd.forEach((field: any) => {
        if (field.labelName === 'Flash') {
          field.value = item.flash;
        }
        if (field.labelName === 'Intervista') {
          field.value = item.intervista;
        }
        if (field.labelName === 'Gadget') {
          field.value = item.gadget ?? false;
        }
        if (field.labelName === 'Prodotti Consegnati') {
          field.data = data;
          field.value = item.id;
          field.quantity = item?.wiseAverangeNumber ?? 0;
          field.autofill = prodottiConfig.autofill;
          field.autoFill = prodottiConfig.autofill;
          field.repeater = prodottiConfig.repeater;
          field.url = prodottiConfig.url;
          this.setBindedLabel(field);
        }
      });
      this.multiGroupData.push(rowToAdd);
    });

    this.multiGroupData.forEach((row) => {
      row.forEach((field: any) => {
        if (field.data && field.data.length > 0) {
          this.setBindedLabel(field);
        }
      });
    });
  }

  initExistingProdottiConsegnati(existingData: any[], inputData: any, repeater: number) {
    if (!existingData?.length) return;
    const adaptedData = existingData.map((dataItem) => ({
      id: dataItem.idProduct,
      name: dataItem.nameProduct,
      wiseAverangeNumber: dataItem.quantity ?? 0,
      intervista: dataItem.intervista ?? false,
      flash: dataItem.flash ?? false,
      gadget: dataItem.gadget ?? false,
    }));

    const hasExistingMultiGroupData =
      this.multiGroupData.length > 0 &&
      this.multiGroupData[0].some((field) => field.labelName === 'Prodotti Consegnati' && field.data && field.data.length > 0);

    if (hasExistingMultiGroupData && this.multiGroupData.length > 1) {
      return;
    }

    this.multiGroupData = [];

    existingData.forEach((item: any, index: number) => {
      const newRow = [
        {
          name: 'idProduct',
          labelName: 'Prodotti Consegnati',
          inputType: InputType.SELECT,
          dataType: DataType.INT,
          value: item.idProduct,
          mandatory: false,
          multiRow: false,
          multiSelect: false,
          quantityField: true,
          quantity: item.quantity ?? 0,
          group: 1,
          data: adaptedData,
          bindedLabel: 'name',
          url: inputData.url,
          autofill: inputData.autofill,
          autoFill: inputData.autoFill ?? inputData.autofill,
          repeater: inputData.repeater ?? existingData.length,
        },
        {
          name: 'intervista',
          labelName: 'Intervista',
          inputType: InputType.CHECKBOX,
          dataType: DataType.BOOLEAN,
          value: item.intervista ?? false,
          mandatory: false,
          multiRow: false,
          multiSelect: false,
          quantityField: false,
          group: 1,
        },
        {
          name: 'flash',
          labelName: 'Flash',
          inputType: InputType.CHECKBOX,
          dataType: DataType.BOOLEAN,
          value: item.flash ?? false,
          mandatory: false,
          multiRow: false,
          multiSelect: false,
          quantityField: false,
          group: 1,
        },
        {
          name: 'gadget',
          labelName: 'Gadget',
          inputType: InputType.CHECKBOX,
          dataType: DataType.BOOLEAN,
          value: item.gadget ?? false,
          mandatory: false,
          multiRow: false,
          multiSelect: false,
          quantityField: false,
          group: 1,
        },
      ];

      this.multiGroupData.push(newRow);
    });

    if (this.multiGroupData.length > 0) {
      this.multiGroupData.forEach((row) => {
        row.forEach((field: any) => {
          if (field.labelName === 'Prodotti Consegnati') {
            field.data = adaptedData;
            this.setBindedLabel(field);
          }
        });
      });
    }
  }

  setBindedLabel(input: any) {
    if (!input.data || !input.data.length) return;

    if (typeof input.value === 'string' && input.inputType === 'select') {
      try {
        const parsed = JSON.parse(input.value);
        if (parsed && typeof parsed === 'object' && parsed.id !== undefined) {
          input.value = parsed.id;
        } else if (typeof parsed === 'number') {
          input.value = parsed;
        }
      } catch {}
    } else if (typeof input.value === 'object' && input.value !== null && input.value.id !== undefined && input.inputType === 'select') {
      input.value = input.value.id;
    }

    const item = input.data[0];

    if (item.hasOwnProperty('fullName') && item.fullName) {
      input.bindedLabel = 'fullName';
      input.data = input.data.sort((a: any, b: any) => a.fullName.localeCompare(b.fullName));
    } else if (item.hasOwnProperty('abbreviationProvince') && item.hasOwnProperty('descriptionProvince')) {
      input.data = input.data.map((dataItem: any) => ({
        ...dataItem,
        fullName: `${dataItem.abbreviationProvince} - ${dataItem.descriptionProvince}`,
      }));
      input.bindedLabel = 'fullName';
      input.data = input.data.sort((a: any, b: any) => a.fullName.localeCompare(b.fullName));
    } else if (item.hasOwnProperty('abbreviationProvince')) {
      input.bindedLabel = 'abbreviationProvince';
      input.data = input.data.sort((a: any, b: any) => a.abbreviationProvince.localeCompare(b.abbreviationProvince));
    } else if (item.hasOwnProperty('businessName')) {
      input.bindedLabel = 'businessName';
    } else if (item.hasOwnProperty('address')) {
      input.bindedLabel = 'address';
    } else if (item.hasOwnProperty('surname') && item.hasOwnProperty('name')) {
      input.data = input.data.map((dataItem: any) => ({
        ...dataItem,
        fullName: dataItem.name + ' ' + dataItem.surname,
      }));
      input.bindedLabel = 'fullName';
    } else {
      input.bindedLabel = 'name';
    }
  }

  getColClass(input: any): string {
    if (input.col) return input.col;
    if ((input.inputType === InputType.TEXTAREA || input.inputType === InputType.TEXT_AREA) && this.activityName !== 'Informazione medica')
      return 'col-12';
    if (input.inputType === InputType.CHECKBOX) return 'col-2';
    if (input.inputType === InputType.SELECT && input.multiRow) return 'col-12';
    return 'col-4';
  }

  onInputChange(event: any, inputIndex: number, type?: string, ...args: any[]) {
    const input = this.inputs[inputIndex];
    let value = event?.target?.value ?? event;

    if (type === 'checkbox') {
      input.value = event.target.checked;
    } else if (type === 'select') {
      if (input.bindValue === 'id' && typeof value === 'object' && value !== null && value.id !== undefined) {
        input.value = value.id;
      } else {
        input.value = value;
      }
      if (input.labelName === 'Fascia Oraria') {
        this.intervalChange.emit(value);
      }
    } else if (type === 'date') {
      input.invalid = false;
      input.errorMessage = '';
      this.isInvalid[inputIndex] = false;

      const raw = value;

      const isValidYMD = (y: number, m: number, d: number) => {
        if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return false;

        const currentYear = new Date().getFullYear();
        if (String(y).length !== 4 || y < 1900 || y > currentYear + 1) return false;
        const jsDate = new Date(y, m - 1, d);
        return jsDate.getFullYear() === y && jsDate.getMonth() === m - 1 && jsDate.getDate() === d;
      };

      if (typeof raw === 'object' && raw?.year && raw?.month && raw?.day) {
        const { year, month, day } = raw;
        if (isValidYMD(year, month, day)) {
          this.formattedDates[inputIndex] = this.dateFormatService.formatDate({ year, month, day }, true);
          input.value = this.formattedDates[inputIndex];
          input.invalid = false;
          input.errorMessage = '';
          this.isInvalid[inputIndex] = false;
        } else {
          input.invalid = true;
          input.errorMessage = 'Data non valida';
          input.value = null;
          this.isInvalid[inputIndex] = true;
        }
      } else if (typeof raw === 'string') {
        const str = raw.trim();
        if (str === '') {
          if (input.mandatory) {
            input.invalid = true;
            input.errorMessage = 'Campo obbligatorio';
            input.value = null;
            this.isInvalid[inputIndex] = true;
          } else {
            input.invalid = false;
            input.errorMessage = '';
            input.value = null;
            this.isInvalid[inputIndex] = false;
          }
        } else {
          const match = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(str);
          if (!match) {
            input.invalid = true;
            input.errorMessage = 'Formato data non valido (usa gg-mm-aaaa)';
            input.value = null;
            this.isInvalid[inputIndex] = true;
          } else {
            const day = Number(match[1]);
            const month = Number(match[2]);
            const year = Number(match[3]);

            if (isValidYMD(year, month, day)) {
              this.formattedDates[inputIndex] = this.dateFormatService.formatDate({ year, month, day }, true);
              input.value = this.formattedDates[inputIndex];
              input.invalid = false;
              input.errorMessage = '';
              this.isInvalid[inputIndex] = false;
            } else {
              input.invalid = true;
              input.errorMessage = 'Data non valida';
              input.value = null;
              this.isInvalid[inputIndex] = true;
            }
          }
        }
      } else {
        if (input.mandatory && (raw === null || raw === undefined || raw === '')) {
          input.invalid = true;
          input.errorMessage = 'Campo obbligatorio';
          this.isInvalid[inputIndex] = true;
        } else {
          input.invalid = false;
          input.errorMessage = '';
          this.isInvalid[inputIndex] = false;
        }
      }
    } else if (type === 'quantity' && args.length > 0) {
      const rowIndex = args[0];
      if (input.multiRowData && input.multiRowData[rowIndex]) {
        const val = value === '' || value === null ? null : +value;
        input.multiRowData[rowIndex].quantity = val;
      }
    } else if (type === 'checkbox-intervista' && args.length > 0) {
      const rowIndex = args[0];
      if (input.multiRowData && input.multiRowData[rowIndex]) {
        input.multiRowData[rowIndex].intervista = event.target.checked;
      }
    } else if (type === 'checkbox-flash' && args.length > 0) {
      const rowIndex = args[0];
      if (input.multiRowData && input.multiRowData[rowIndex]) {
        input.multiRowData[rowIndex].flash = event.target.checked;
      }
    } else if (type === 'checkbox-gadget' && args.length > 0) {
      const rowIndex = args[0];
      if (input.multiRowData && input.multiRowData[rowIndex]) {
        input.multiRowData[rowIndex].gadget = event.target.checked;
      }
    } else {
      input.value = value;
    }

    this.handleFieldDependencies(input, inputIndex);
    this.validateInput(input, inputIndex);
    this.emitForm();
    this.validateFormAndEmit();
  }

  handleFieldDependencies(input: any, inputIndex: number) {
    if (input.labelName === 'Medico Visitato') {
      if (this.mode === 'modifica') {
        const doctorChanged = input.value !== this.savedDoctorId;
        if (doctorChanged && !this.doctorChangedByUser) {
          this.prodottiConsegnatiFetchId++;
        }
        this.doctorChangedByUser = doctorChanged;
      }
      this.idDoctor = input.value;
      const medico = input.data?.find((d: any) => d.id === input.value);

      if (medico) {
        // Recupero l'indirizzo selezionato
        const addressSelectedId = this.inputs.find((d: any) => d.name === 'indirizzoDiVisita')?.value;
        const exists = medico.addresses?.some((addr: any) => addr.id === addressSelectedId);

        if (!exists && addressSelectedId) {
          this.attivitaService
            .getStructuresSelectV1({ addressId: addressSelectedId })
            .pipe(
              tap((res: any) => {
                const newAddresses = res?.data?.list?.flatMap((item: any) => item.addresses) || [];
                // Concateno gli addresses nuovi
                medico.addresses = medico.addresses ? [...medico.addresses, ...newAddresses] : newAddresses;
              })
            )
            .subscribe({
              next: () => {
                // Emesso sempre dopo aver aggiornato gli addresses
                this.attivitaService.$addressSubject.next({
                  id: input.value,
                  addresses: medico.addresses,
                  profile: medico?.profile,
                  speciality: medico?.speciality,
                });
              },
              error: (err) => console.error('Errore recupero indirizzo:', err),
            });
        } else {
          // Se l'indirizzo esiste già, emetto direttamente
          this.attivitaService.$addressSubject.next({
            id: input.value,
            addresses: medico.addresses,
            profile: medico?.profile,
            speciality: medico?.speciality,
          });
        }
      }

      if ((this.activityFlowType === 'struttura' || this.activityFlowType === 'medici') && !this.hasProvinceBeenNullOnce) {
        this.attivitaService.$provinceSubject.next(null);
      }
    }

    if (input.labelName === 'Indirizzo Di Visita') {
      this.onAddressChange(input.data, input.value);
    }

    if (this.activityName === 'Mis' && input.labelName === 'Medici') {
      this.populateGivenProducts();
    }
  }

  onAddressChange(addresses: any, currentValue?: any) {
    this.attivitaService.$provinceSubject.next(currentValue ? addresses : null);
  }

  populateGivenProducts() {
    this.presentedProducts = this.attivitaService.$presentedProductSubjects.value;
    this.isPresentedProducts = true;
  }

  onInputMultiGroupChange(event: any, groupIndex: number, inputIndex: number, type?: string) {
    const group = this.multiGroupData[groupIndex];
    const input = group[inputIndex];
    let value = event?.target?.value ?? event;

    if (type === 'checkbox') {
      input.value = event.target.checked;
    } else if (type === 'select') {
      if (input.bindValue === 'id' && typeof value === 'object' && value !== null && value.id !== undefined) {
        input.value = value.id;
      } else {
        input.value = value;
      }
    } else if (type === 'date') {
      input.value = value;
    } else if (type === 'quantity') {
      const val = value === '' || value === null ? null : +value;
      input.quantity = val;
    } else {
      input.value = value;
    }

    if (input.labelName === 'Medici') {
      this.onDoctorGroupChange(value, groupIndex, inputIndex);
    }

    this.validateInput(input, inputIndex);
    this.emitForm();
    this.validateFormAndEmit();
  }

  onDoctorGroupChange(doctorId: number, parentIndex: number, index: number) {
    const selectedDoctor = this.multiGroupData[parentIndex][index].data.find((doctor: any) => doctor.id == doctorId);
    this.attivitaService.$addressSubject.next({
      id: doctorId,
      addresses: selectedDoctor?.addresses,
      profile: selectedDoctor?.profile,
      speciality: selectedDoctor?.speciality,
      parentIndex: parentIndex,
      index: index,
    });
  }

  addRow(inputIndex: number) {
    const input = this.inputs[inputIndex];
    if (!input.multiRowData) input.multiRowData = [];

    input.multiRowData.push({
      name: input.labelName,
      value: '',
      mandatory: input.mandatory,
      multiSelect: input.multiSelect,
      inputType: input.inputType,
      dataType: input.dataType,
      multiRow: input.multiRow,
      quantityField: input.quantityField,
      quantity: 0,
      intervista: false,
      flash: false,
    });
    this.emitForm();
    this.validateFormAndEmit();
  }

  removeRow(inputIndex: number, rowIndex: number) {
    const input = this.inputs[inputIndex];
    if (input.multiRowData) {
      input.multiRowData.splice(rowIndex, 1);
    }
    this.emitForm();
    this.validateFormAndEmit();
  }

  addMultiGroupRow() {
    if (this.multiGroupData.length > 0) {
      const newRow = this.multiGroupData[0].map((el: any) => ({
        ...el,
        value: el.dataType === DataType.BOOLEAN ? false : null,
        quantity: el.quantityField ? 0 : el.quantity,
      }));
      this.multiGroupData.push(newRow);
      this.emitForm();
      this.validateFormAndEmit();
    }
  }

  removeMultiGroupRow(groupIndex: number) {
    this.multiGroupData.splice(groupIndex, 1);
    this.emitForm();
    this.validateFormAndEmit();
  }

  onSearch(event: any) {
    if (!event || !event.hasOwnProperty('term')) {
      return;
    }

    this.searchTerm = event.term ?? '';
    const searchTerm$ = new Observable<string>((observer) => {
      observer.next(this.searchTerm);
      observer.complete();
    })
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        if (this.searchTerm.length > 3 || this.searchTerm.length === 0) {
          this.inputs.forEach((input) => {
            if (!Array.isArray(input) && input.url) {
              this.fetchInputData(input);
            }
          });
        }
      });
  }

  validateInput(input: any, index: number) {
    let isValid = true;

    if (input.mandatory) {
      if (input.inputType === InputType.CHECKBOX) {
        isValid = input.value === true || input.value === 'True';
      } else if (input.inputType === InputType.SELECT && input.multiRow) {
        isValid = input.multiRowData && input.multiRowData.length > 0 && input.multiRowData.some((row: any) => row.value && row.value !== '');
      } else if (Array.isArray(input.value)) {
        isValid = input.value.length > 0;
      } else {
        isValid = input.value !== null && input.value !== undefined && input.value !== '';
      }
    }

    this.isInvalid[index] = !isValid;
    return isValid;
  }

  validateForm() {
    let allValid = true;
    const errors: string[] = [];

    this.inputs.forEach((input, i) => {
      if (!Array.isArray(input)) {
        const isValid = this.validateInput(input, i);
        if (!isValid && input.mandatory) {
          allValid = false;
          errors.push(`Campo "${input.labelName}" è obbligatorio`);
        }
      }
    });

    if (this.multiGroupData.length > 0) {
      this.multiGroupData.forEach((group, groupIndex) => {
        group.forEach((item: any) => {
          if (item.mandatory) {
            let isValid = true;
            if (item.inputType === InputType.CHECKBOX) {
              isValid = item.value === true || item.value === 'True';
            } else if (Array.isArray(item.value)) {
              isValid = item.value.length > 0;
            } else {
              isValid = item.value !== null && item.value !== undefined && item.value !== '';
            }

            if (!isValid) {
              allValid = false;
              errors.push(`Campo "${item.labelName}" nel gruppo ${groupIndex + 1} è obbligatorio`);
            }
          }
        });
      });
    }
    if (this.activityName === 'Mis' && this.showMisForm) {
      Object.values(this.misDoctorsEssaysSelected).forEach((essays: any) => {
        essays.forEach((saggio: any) => {
          if (saggio.quantita !== null && saggio.quantita < 0) {
            allValid = false;
            errors.push('La quantità di un prodotto non può essere negativa');
          }
        });
      });
    }

    return { isValid: allValid, errors };
  }

  validateFormAndEmit() {
    const validation = this.validateForm();
    this.isFormValid.emit(validation.isValid);
    this.attivitaService.updateFormValidation(validation.isValid, validation.errors);
    return validation;
  }

  getFormOutput() {
    const body: any = {};

    this.inputs.forEach((input, index) => {
      if (Array.isArray(input)) {
        const result = input.map((group: any) => {
          if (Array.isArray(group)) {
            const objectFields: any = {};
            group.forEach((item: any) => {
              if (item.value !== '' && item.value !== null) {
                objectFields[item.name || item.labelName] = item.value;
                if (item.quantity) objectFields.quantity = item.quantity;
              }
            });
            return objectFields;
          }
          return group;
        });

        const groupName = Array.isArray(input[0]) && input[0].find((item: any) => item.groupName !== '')?.groupName;
        if (groupName) {
          body[groupName] = result;
        }
      } else {
        if (
          this.activityName === 'Mis' &&
          (input.labelName === 'Prodotti Presentati' || input.labelName === 'Medici' || input.labelName === 'Saggi Consegnati')
        ) {
          return;
        }

        const fieldName = input.name || input.labelName;
        let value = input.value === undefined ? null : input.value;

        if (input.inputType === InputType.DATE && value && typeof value === 'string') {
          value = this.convertDateForOutput(value);
        }

        if (fieldName === 'Profilo' || fieldName === 'Specialità') {
          return;
        }

        if (this.activityName === 'Mis' && (fieldName === 'idProduct' || fieldName === 'idDoctor')) {
          return;
        }

        body[fieldName] = value;
      }
    });
    if (this.activityName === 'Mis' && this.showMisForm) {
      body['prodottiPresentati'] = this.misProdottiSelezionati.map((p) => p.variant?.id);
      body['participatingDoctors'] = this.selectedDoctors;
      const doctorsData = this.misDoctorsRows
        .filter((row) => row.doctorIds && row.doctorIds.length > 0)
        .map((row) => {
          return row.doctorIds.flatMap((medicoId: number) => {
            const saggiForMedico = this.misDoctorsEssaysSelected[medicoId] ?? [];
            return saggiForMedico.map((saggio) => ({
              idDoctor: medicoId,
              idProduct: saggio.saggioId,
              quantity: saggio.quantita,
            }));
          });
        })
        .flat();

      body['productDoctor'] = doctorsData;
    }

    if (this.multiGroupData.length > 0) {
      const prodottiConsegnati: any[] = [];

      this.multiGroupData.forEach((group, index) => {
        const groupData: any = {};
        let hasValidData = false;

        group.forEach((item: any) => {
          if (item.name === 'idProduct' && item.value !== '' && item.value !== null) {
            groupData.idProduct = item.value;
            groupData.quantity = item.quantity;
            hasValidData = true;
          } else if (item.name === 'intervista') {
            groupData.intervista = item.value ? true : false;
          } else if (item.name === 'flash') {
            groupData.flash = item.value ? true : false;
          } else if (item.name === 'gadget') {
            groupData.gadget = item.value ? true : false;
          } else if (item.value !== '' && item.value !== null) {
            groupData[item.name || item.labelName] = item.value === undefined ? null : item.value;
            if (item.quantity) groupData.quantity = item.quantity;
          }
        });

        if (hasValidData) {
          prodottiConsegnati.push(groupData);
        } else {
          const groupName = group.find((item: any) => item.groupName !== '')?.groupName;
          if (groupName) {
            if (!body[groupName]) body[groupName] = [];
            body[groupName].push(groupData);
          }
        }
      });

      if (prodottiConsegnati.length > 0) {
        body['prodottiConsegnati'] = prodottiConsegnati;
      }
    }

    if (this.presentedProducts && this.presentedProducts.length > 0) {
      const presentedProductsAsMultiRow = this.presentedProducts
        .filter((product) => product.id)
        .map((product) => ({
          idProduct: product.id,
          quantity: product.quantity ?? 0,
        }));
      body['Saggi Consegnati'] = presentedProductsAsMultiRow;
    }

    return body;
  }

  public getFormData() {
    this.validateForm();
    return {
      inputs: this.inputs,
      multiGroupData: this.multiGroupData,
      output: this.getFormOutput(),
      isValid: !this.isInvalid.some((invalid) => invalid),
    };
  }

  emitForm() {
    const formData = {
      inputs: this.inputs,
      multiGroupData: this.multiGroupData,
      output: this.getFormOutput(),
    };
    this.formChange.emit(formData);
    this.cdr.detectChanges();
  }

  getGroupArray(group: any): any[] {
    return Array.isArray(group) ? group : [];
  }

  returnType(value: any) {
    return typeof value;
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  initMisLogic() {
    if (this.activityName === 'Mis') {
      this.initMisInputs();
    }
  }

  initMisDataFromInputs() {
    const prodottiInput = this.inputs.find((i) => i.labelName === 'Prodotti Presentati');
    const saggiInput = this.inputs.find((i) => i.labelName === 'Saggi Consegnati');
    const mediciInput = this.inputs.find((i) => i.labelName === 'Medici');

    if (prodottiInput && Array.isArray(prodottiInput.value) && prodottiInput.url) {
      this.fetchMisData(prodottiInput.url, { isf: this.user?.id }).subscribe((response) => {
        const allProducts = response?.data?.misDetail ?? [];

        this.misProducts = allProducts;

        this.misProdottiSelezionati = allProducts.filter((prod: any) => prodottiInput.value.includes(prod.variant?.id));

        if (this.mode === 'visualizza') {
          this.misProdottiPresentatiView = [...this.misProdottiSelezionati];
        }

        if (this.mode !== 'visualizza') {
          this.updateMisSaggiForAllMedici();
        }

        this.cdr.detectChanges();
      });
    }

    if (saggiInput && Array.isArray(saggiInput.value)) {
      this.misSaggiConsegnatiView = saggiInput.value;
      this.seedMisDoctorsFromSaggi(saggiInput.value);
      this.populateMisSaggiFromInput(saggiInput.value);
      this.cdr.detectChanges();

      if (mediciInput?.url) {
        if (Array.isArray(mediciInput.value)) {
          this.selectedDoctors = [...mediciInput.value];
        }
        this.fetchMisData(mediciInput.url, { isf: this.user?.id }, this.getMisMediciIncludedIds(mediciInput)).subscribe((response) => {
          this.mergeMisDoctors(response?.data?.list ?? []);
          this.filteredDoctors = [...this.misDoctors];

          for (let i = 0; i < this.misDoctorsRows.length; i++) {
            this.filteredDoctorsPerRow[i] = this.misDoctors.filter((doctor) => doctor.isNotWiseEnabled === false);
          }

          if (this.mode !== 'visualizza') {
            this.updateMisSaggiForAllMedici();
          }

          this.cdr.detectChanges();
        });
      }
    }
  }

  initMisInputs() {
    const prodottiInput = this.inputs.find((i) => i.labelName === 'Prodotti Presentati');
    const saggiInput = this.inputs.find((i) => i.labelName === 'Saggi Consegnati');
    const mediciInput = this.inputs.find((i) => i.labelName === 'Medici');

    if (!prodottiInput && !saggiInput && !mediciInput) {
      this.showMisForm = false;
      return;
    }

    this.showMisForm = true;

    if (prodottiInput && prodottiInput.url) {
      this.fetchMisData(prodottiInput.url, { userId: this.user?.id }).subscribe((response) => {
        this.misProducts = response?.data?.misDetail ?? [];
      });
    }
    if (saggiInput && saggiInput.url && this.mode !== 'visualizza') {
      this.fetchMisData(saggiInput.url, { idUser: this.user?.id }).subscribe((response: any) => {
        this.listSaggi = response?.data?.list ?? [];
      });
    }

    if (mediciInput?.url && this.mode === 'nuovo') {
      if (Array.isArray(mediciInput.value)) {
        this.selectedDoctors = [...mediciInput.value];
      }
      this.fetchMisData(mediciInput.url, { isf: this.user?.id }, this.getMisMediciIncludedIds(mediciInput)).subscribe((response) => {
        this.mergeMisDoctors(response?.data?.list ?? []);
        this.filteredDoctors = [...this.misDoctors];

        if (saggiInput && Array.isArray(saggiInput.value)) {
          const mediciConSaggi = [...new Set(saggiInput.value.map((s: any) => s.idDoctor))];
          this.selectedDoctorsList = this.misDoctors.filter((d) => mediciConSaggi.includes(d.id));
        }

        this.misDoctorsRows = [{ doctorIds: this.selectedDoctorsList.map((doc) => doc.id) }];

        this.misDoctorsEssaysSelected = {};

        if (saggiInput && Array.isArray(saggiInput.value)) {
          saggiInput.value.forEach((saggio: any) => {
            if (!this.misDoctorsEssaysSelected[saggio.idDoctor]) {
              this.misDoctorsEssaysSelected[saggio.idDoctor] = [];
            }

            const newRow = {
              saggioId: saggio.idProduct,
              id: saggio.idProduct,
              name: saggio.nameProduct,
              quantita: saggio.quantity,
              locked: this.mode === 'visualizza',
            };

            this.misDoctorsEssaysSelected[saggio.idDoctor].push(newRow);
          });
        }

        this.updateMisSaggiForAllMedici();
        this.cdr.detectChanges();
      });
    }
  }

  fetchMisData(url: string, params: any, includedIds?: any) {
    const apiUrl = new URL(env.baseUrl + url);
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null) {
        apiUrl.searchParams.set(key, params[key]);
      }
    });

    if (includedIds !== undefined) {
      this.appendMisIncludedIds(url, apiUrl, includedIds);
    }

    return this.http.get<any>(apiUrl.toString());
  }

  onMisProdottoToggle(prodotto: any, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      if (!this.misProdottiSelezionati.some((p) => p.variant?.id === prodotto.variant?.id)) {
        this.misProdottiSelezionati.push(prodotto);
      }
    } else {
      this.misProdottiSelezionati = this.misProdottiSelezionati.filter((p) => p.variant?.id !== prodotto.variant?.id);
    }
    this.updateMisSaggiForAllMedici();
  }

  addMisMedicoRow() {
    const newRowIndex = this.misDoctorsRows.length;
    this.misDoctorsRows.push({ doctorIds: [] });
    this.filteredDoctorsPerRow[newRowIndex] = [...this.selectedDoctorsList];
    this.emitForm();
  }

  onMisMedicoSelect(rowIndex: number, medicoIds: number[] | null) {
    this.misDoctorsRows[rowIndex].doctorIds = medicoIds ?? [];

    for (const medicoId of this.misDoctorsRows[rowIndex].doctorIds) {
      if (!this.misDoctorsEssaysSelected[medicoId]) {
        this.misDoctorsEssaysSelected[medicoId] = this.misProdottiSelezionati.map((prod) => ({
          saggioId: prod.variant?.id,
          id: prod.variant?.id ?? prod?.id,
          name: prod.variant?.name ?? prod?.name,
          quantita: 1,
          locked: true,
        }));
      }
      this.loadMisSaggiForMedico(medicoId);
    }
    this.updateMisSaggiForAllMedici();
  }

  removeMisMedicoRow(rowIndex: number) {
    const row = this.misDoctorsRows[rowIndex];
    for (const doctorId of row.doctorIds ?? []) {
      delete this.misDoctorsEssays[doctorId];
      delete this.misDoctorsEssaysSelected[doctorId];
    }
    this.misDoctorsRows.splice(rowIndex, 1);
    this.emitForm();
  }

  loadMisSaggiForMedico(idDoctor: number) {
    this.misDoctorsEssays[idDoctor] = [];

    const prodottiInput = this.inputs.find((i) => i.labelName === 'Saggi Consegnati');
    if (prodottiInput && prodottiInput.url) {
      this.misDoctorsEssays[idDoctor] = this.listSaggi;
      this.updateMisSaggiForAllMedici();
      this.cdr.detectChanges();
    }
  }

  addMisSaggioRow(doctorId: number) {
    if (!this.misDoctorsEssaysSelected[doctorId]) {
      this.misDoctorsEssaysSelected[doctorId] = [];
    }
    this.misDoctorsEssaysSelected[doctorId].push({ saggioId: null, quantita: 1, locked: false });
    this.emitForm();
  }

  isUnderstockSelected(groupInput: any): boolean {
    if (!groupInput || !groupInput.value || !groupInput.data) return false;

    const selectedIds = Array.isArray(groupInput.value) ? groupInput.value : [groupInput.value];
    return groupInput.data.some((item: { id: any; underStock: any }) => selectedIds.includes(item.id) && item.underStock);
  }

  removeMisSaggioRow(doctorId: number, index: number) {
    if (this.misDoctorsEssaysSelected[doctorId]) {
      const removed = this.misDoctorsEssaysSelected[doctorId].splice(index, 1);
      this.emitForm();
    } else {
    }
  }

  updateMisSaggio(doctorIds: number[], index: number, saggioId: number) {
    doctorIds.forEach((id) => {
      if (this.misDoctorsEssaysSelected[id] && this.misDoctorsEssaysSelected[id][index]) {
        this.misDoctorsEssaysSelected[id][index].saggioId = saggioId;
      }
    });
    this.emitForm();
  }

  updateMisQuantita(doctorIds: number[], index: number, quantita: number) {
    const val = quantita === null || quantita === undefined || (quantita as any) === '' ? null : quantita;
    if (val !== null && val < 0) {
      this.toastr.error('La quantità di un prodotto non può essere negativa', 'Attenzione!');
    }
    doctorIds.forEach((id) => {
      if (this.misDoctorsEssaysSelected[id] && this.misDoctorsEssaysSelected[id][index]) {
        this.misDoctorsEssaysSelected[id][index].quantita = val;
      }
    });
  }

  onParticipatingDoctorsChange(selectedIds: number[]) {
    this.selectedDoctorsList = this.misDoctors.filter((d) => selectedIds.includes(d.id));

    this.misDoctorsRows.forEach((row) => {
      if (row.doctorIds && row.doctorIds.length > 0) {
        row.doctorIds = row.doctorIds.filter((id: number) => selectedIds.includes(id));
      }
    });
    this.emitForm();
  }

  isMisProdottoSelezionato(prodotto: any): boolean {
    return this.misProdottiSelezionati.some((p) => p.variant?.id === prodotto.variant?.id);
  }

  getMisMedicoName(doctorId: number | null): string {
    if (doctorId == null) return '';

    const normalizedId = Number(doctorId);
    const doctor = this.misDoctors.find((m) => Number(m.id) === normalizedId);
    if (doctor?.fullName) return doctor.fullName;

    const saggioWithName = this.misSaggiConsegnatiView.find(
      (s: any) => Number(s.idDoctor) === normalizedId && s.fullName
    );
    return saggioWithName?.fullName ?? String(doctorId);
  }

  updateMisSaggiForAllMedici() {
    const selectedIds = this.misProdottiSelezionati.map((prod) => prod.variant?.id);

    for (const row of this.misDoctorsRows) {
      for (const doctorId of row.doctorIds ?? []) {
        const prevRows = this.misDoctorsEssaysSelected[doctorId] ?? [];

        const remainingPrevRows = prevRows.filter((r) => !selectedIds.includes(r.saggioId));

        const lockedRows = selectedIds.map((id) => {
          const existingRow = prevRows.find((r) => r.saggioId === id);
          const variant = this.misProducts.find((p) => p.variant?.id === id)?.variant ?? { id };
          return {
            saggioId: id,
            id: id,
            name: variant.name,
            variant,
            quantita: existingRow ? existingRow.quantita : 1,
            locked: true,
          };
        });

        this.misDoctorsEssaysSelected[doctorId] = [...lockedRows, ...remainingPrevRows];
      }
    }

    this.updateUniqueSaggi();
    this.emitForm();
  }

  onQuantityBlur(inputIndex: number, rowIndex: number) {
    const input = this.inputs[inputIndex];
    if (input && input.multiRowData && input.multiRowData[rowIndex]) {
      if (input.multiRowData[rowIndex].quantity === null || input.multiRowData[rowIndex].quantity === undefined) {
        input.multiRowData[rowIndex].quantity = 0;
      }
    }
    this.emitForm();
  }

  onMultiGroupQuantityBlur(groupIndex: number, inputIndex: number) {
    const group = this.multiGroupData[groupIndex];
    const input = group[inputIndex];
    if (input) {
      if (input.quantity === null || input.quantity === undefined) {
        input.quantity = 0;
      }
    }
    this.emitForm();
  }

  onMisQuantitaBlur(medicoId: number, index: number) {
    const saggioRow = this.misDoctorsEssaysSelected[medicoId]?.[index];
    if (saggioRow) {
      if (saggioRow.quantita === null || saggioRow.quantita === undefined) {
        saggioRow.quantita = 0;
      } else if (saggioRow.quantita < 0) {
        this.toastr.error('La quantità di un prodotto non può essere negativa', 'Attenzione!');
      }
    }
    this.emitForm();
  }

  onPresentedProductQuantityBlur(index: number) {
    if (this.presentedProducts[index]) {
      if (this.presentedProducts[index].quantity === null || this.presentedProducts[index].quantity === undefined) {
        this.presentedProducts[index].quantity = 0;
      }
    }
    this.emitForm();
  }

  updateUniqueSaggi() {
    let allSaggi = Object.values(this.misDoctorsEssays).flat();

    if (!allSaggi || allSaggi.length === 0) {
      allSaggi = Object.values(this.misDoctorsEssaysSelected).flat();
    }

    const map = new Map<number, any>();
    allSaggi.forEach((saggio) => {
      const id = saggio.variant?.id ?? saggio.saggioId ?? saggio.id;

      if (id != null && !map.has(id)) {
        map.set(id, {
          ...saggio,
          id,
          name: saggio.name ?? saggio.nameProduct ?? saggio.variant?.name,
        });
      }
    });

    this.misDoctorsEssaysUnique = Array.from(map.values());
  }

  convertDateForOutput(dateValue: string): string {
    if (!dateValue) return '';

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }

    if (/^\d{2}-\d{2}-\d{4}$/.test(dateValue)) {
      const [day, month, year] = dateValue.split('-');
      const converted = `${year}-${month}-${day}`;
      return converted;
    }

    return dateValue;
  }

  override ngOnDestroy(): void {
    this.attivitaService.$addressSubject.next(null);
    this.attivitaService.$provinceSubject.next(null);
    this.attivitaService.resetFormValidation();
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.resetFormState();
  }
}
